import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { startCheckout } from "@/lib/payments";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  UsersThree, Camera, PuzzlePiece, GlobeHemisphereWest, HandHeart, Megaphone, Handshake,
  ArrowRight, Heart, Buildings, Lightbulb, Bug, ShareNetwork, EnvelopeSimple, ShieldCheck,
  Database, BookOpen, Users, FacebookLogo, InstagramLogo, XLogo, LinkedinLogo, YoutubeLogo,
  CheckCircle, Sparkle,
} from "@phosphor-icons/react";

const WHY = [
  { icon: UsersThree, title: "Connect", body: "Help us build better tools for classmates, students, teachers and alumni to discover and reconnect with one another." },
  { icon: Camera, title: "Preserve", body: "Help preserve school and college histories, photographs, stories, memories, reunions and institutional timelines." },
  { icon: PuzzlePiece, title: "Build", body: "Support the development of new features, AI tools, search, messaging, communities, events, profiles and educational resources." },
  { icon: GlobeHemisphereWest, title: "Expand", body: "Help AlumniPulse™ bring more schools, colleges, universities and alumni communities online around the world." },
];

const WHERE = [
  { icon: PuzzlePiece, title: "Platform Development", body: "Better profiles, groups, search, messaging, events, reunions, AI tools and community features." },
  { icon: Database, title: "Infrastructure", body: "Servers, databases, storage, security, reliability, backups and global delivery." },
  { icon: Buildings, title: "Educational Institutions", body: "Researching, organizing, verifying and expanding the global directory of schools, colleges and universities." },
  { icon: BookOpen, title: "Memory Preservation", body: "Tools for preserving old photographs, buildings, uniforms, classroom memories, stories and yearbooks." },
  { icon: GlobeHemisphereWest, title: "Global Expansion", body: "Bringing institutions and alumni communities from more countries and regions onto AlumniPulse™." },
  { icon: Users, title: "Community Experience", body: "Improving accessibility, moderation, communication, discovery, support and the overall experience." },
];

const OTHER = [
  { icon: Camera, title: "Contribute Memories", body: "Share old photographs, documents, stories, school memorabilia and institutional history.", to: "/memory-lane" },
  { icon: HandHeart, title: "Volunteer", body: "Help with research, content, translation, institution information or community outreach.", to: "/contact" },
  { icon: Lightbulb, title: "Suggest Features", body: "Tell us what tools would make AlumniPulse™ more useful for your school or alumni group.", to: "/contact" },
  { icon: Bug, title: "Report an Issue", body: "Help us improve by reporting technical problems, incorrect information or inappropriate content.", to: "/contact" },
  { icon: Buildings, title: "Invite Your Institution", body: "Know a school, college or alumni association that should be on AlumniPulse™? Invite them.", to: "/contact" },
  { icon: ShareNetwork, title: "Share With Classmates", body: "Send AlumniPulse™ to your former classmates, teachers, friends and alumni groups.", to: "share" },
];

const SOCIALS = [
  { icon: FacebookLogo, href: "#" }, { icon: InstagramLogo, href: "#" }, { icon: XLogo, href: "#" },
  { icon: LinkedinLogo, href: "#" }, { icon: YoutubeLogo, href: "#" },
];

const AMOUNTS = [["contribute_10", "$10"], ["contribute_25", "$25"], ["contribute_50", "$50"], ["contribute_100", "$100"]];

const Section = ({ children, className = "" }) => (
  <motion.section initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.55 }} className={className}>
    {children}
  </motion.section>
);

export default function Support() {
  useSeo("Support AlumniPulse™ | Help Connect Generations Worldwide", "Support AlumniPulse™, a global platform connecting schools, colleges, universities, students, teachers and alumni while preserving educational memories.");
  const { user } = useAuth();
  const [amt, setAmt] = useState("contribute_25");
  const [open, setOpen] = useState(false);

  const share = () => {
    const url = window.location.origin;
    if (navigator.share) navigator.share({ title: "AlumniPulse™", text: "Reconnect with your classmates on AlumniPulse™", url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Link copied — share it with your classmates!"); }
  };
  const contribute = () => startCheckout(amt, user);

  return (
    <div className="bg-background" data-testid="support-page">
      {/* Hero */}
      <section className="border-b border-border/60 bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:py-20">
          <div>
            <p className="overline mb-4 inline-flex items-center gap-1.5 text-primary-foreground/70"><Sparkle size={14} weight="fill" /> Support AlumniPulse™</p>
            <h1 className="font-heading text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">Help Connect <span className="italic font-medium">Generations.</span></h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-primary-foreground/85">Support the technology, communities and memories that bring students, teachers, alumni and educational institutions together around the world.</p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-primary-foreground/70">Your support helps us improve the platform, develop new features, preserve educational memories, expand our global institution directory, and make it easier for generations to reconnect.</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => setOpen(true)} className="rounded-full gap-2 bg-white px-7 text-primary hover:bg-white/90" data-testid="hero-contribute-now"><Heart size={18} weight="fill" /> Contribute Now</Button>
              <Link to="/memory-lane"><Button size="lg" variant="outline" className="rounded-full gap-2 border-white/40 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white" data-testid="hero-contribute-memory"><Camera size={18} /> Contribute a Memory</Button></Link>
              <Link to="/"><Button size="lg" variant="ghost" className="rounded-full px-6 text-primary-foreground/80 hover:bg-white/10 hover:text-white">Explore AlumniPulse</Button></Link>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="rounded-3xl border border-white/15 bg-white/5 p-8 backdrop-blur">
              <div className="grid grid-cols-2 gap-4">
                {WHY.map((w) => (
                  <div key={w.title} className="rounded-2xl bg-white/[0.06] p-5">
                    <w.icon size={26} weight="duotone" className="text-white/90" />
                    <p className="mt-3 font-heading text-lg">{w.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5">
        {/* Why support */}
        <Section className="py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Why Your Support Matters</h2>
            <p className="mt-3 text-muted-foreground">Building a global education and alumni network takes technology, infrastructure, research, content, security and continuous innovation. Support from individuals, alumni groups, institutions and communities helps AlumniPulse™ keep growing.</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
                <span className="inline-grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary"><w.icon size={24} weight="duotone" /></span>
                <h3 className="mt-4 font-heading text-lg font-medium">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Four ways */}
        <Section className="py-8">
          <h2 className="text-center font-heading text-3xl font-light tracking-tight sm:text-4xl">Choose How You Want to Support AlumniPulse™</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Heart, title: "Contribute Now", body: "Make a one-time contribution to help us develop features, maintain infrastructure, preserve memories and expand AlumniPulse™ worldwide.", cta: "Contribute Now", action: () => setOpen(true), testid: "way-contribute", solid: true },
              { icon: HandHeart, title: "Become a Supporter", body: "Choose a recurring monthly or annual support plan and become part of the community helping AlumniPulse™ grow over time.", cta: "View Support Plans", action: () => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth" }), testid: "way-supporter" },
              { icon: Handshake, title: "Partner With Us", body: "Institutions, alumni associations, organizations and community groups can partner with AlumniPulse™ on programs, content and global expansion.", cta: "Partner With Us", to: "/contact", testid: "way-partner" },
              { icon: Megaphone, title: "Spread the Word", body: "The easiest way to help is to tell your classmates, teachers, alumni associations, schools, colleges and universities about AlumniPulse™.", cta: "Share AlumniPulse", action: share, testid: "way-share" },
            ].map((c, i) => (
              <div key={c.title} className="flex flex-col rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
                <span className={`inline-grid h-12 w-12 place-items-center rounded-xl ${["bg-secondary text-secondary-foreground", "bg-accent text-primary", "bg-primary/10 text-primary", "bg-amber-500/15 text-amber-600"][i]}`}><c.icon size={24} weight="duotone" /></span>
                <h3 className="mt-4 font-heading text-lg font-medium">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                {c.to ? (
                  <Link to={c.to}><Button className="mt-5 w-full rounded-full gap-1.5" variant={c.solid ? "default" : "outline"} data-testid={c.testid}>{c.cta}</Button></Link>
                ) : (
                  <Button onClick={c.action} className="mt-5 w-full rounded-full gap-1.5" variant={c.solid ? "default" : "outline"} data-testid={c.testid}>{c.cta}</Button>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Where support goes */}
        <Section className="py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Building the Future of Alumni Connections</h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHERE.map((w) => (
              <div key={w.title} className="flex gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                <span className="inline-grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-primary"><w.icon size={22} weight="duotone" /></span>
                <div><h3 className="font-heading text-base font-medium">{w.title}</h3><p className="mt-1 text-sm text-muted-foreground">{w.body}</p></div>
              </div>
            ))}
          </div>
        </Section>

        {/* Memory project */}
        <Section className="pb-16">
          <div className="rounded-[2rem] border border-secondary/25 bg-accent/40 p-8 sm:p-12">
            <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <p className="overline text-secondary mb-2">The Memory Project</p>
                <h2 className="font-heading text-3xl font-light tracking-tight">Help Preserve the Memories of Education</h2>
                <p className="mt-3 text-muted-foreground">Old school photographs, uniforms, textbooks, classrooms, buildings, yearbooks and reunion photographs are often scattered across personal collections and forgotten archives. AlumniPulse™ is creating a growing digital memory collection where communities can preserve these stories for future generations.</p>
                <p className="mt-3 text-sm text-secondary">You can contribute photographs, stories, historical information or memories — without making a financial contribution.</p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/memory-lane"><Button size="lg" className="w-full rounded-full gap-2" data-testid="memory-project-cta"><Camera size={18} /> Contribute a Memory</Button></Link>
                <Button size="lg" variant="outline" onClick={() => setOpen(true)} className="w-full rounded-full gap-2"><Heart size={18} weight="fill" /> Contribute Now</Button>
              </div>
            </div>
          </div>
        </Section>

        {/* Other ways */}
        <Section className="pb-16">
          <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">There Are Many Ways to Help</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {OTHER.map((o) => {
              const Inner = (
                <div className="flex h-full gap-4 rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-colors hover:border-secondary/40">
                  <span className="inline-grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent text-primary"><o.icon size={22} weight="duotone" /></span>
                  <div><h3 className="font-heading text-base font-medium">{o.title}</h3><p className="mt-1 text-sm text-muted-foreground">{o.body}</p></div>
                </div>
              );
              return o.to === "share"
                ? <button key={o.title} onClick={share} className="text-left" data-testid="other-share">{Inner}</button>
                : <Link key={o.title} to={o.to}>{Inner}</Link>;
            })}
          </div>
        </Section>

        {/* For institutions */}
        <Section className="pb-16">
          <div className="rounded-[2rem] bg-primary/[0.04] border border-border/60 p-8 sm:p-12">
            <div className="grid items-center gap-6 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <p className="overline text-secondary mb-2">For Institutions</p>
                <h2 className="font-heading text-3xl font-light tracking-tight">Bring Your Institution Into the Global Network</h2>
                <p className="mt-3 text-muted-foreground">Is your school, college or university already on AlumniPulse™? Help build its digital presence by adding its history, campuses, departments, classes, alumni communities, events and milestones. If it isn't listed yet, suggest it to us.</p>
              </div>
              <div className="flex flex-col gap-3">
                <Link to="/contact"><Button className="w-full rounded-full gap-1.5" data-testid="suggest-inst-cta"><Buildings size={16} /> Suggest an Institution</Button></Link>
                <Link to="/institutions"><Button variant="outline" className="w-full rounded-full gap-1.5">Claim / Manage Institution</Button></Link>
              </div>
            </div>
          </div>
        </Section>

        {/* Support plans */}
        <Section className="pb-16" >
          <div id="plans" className="scroll-mt-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Become Part of the Journey</h2>
            <p className="mt-3 text-muted-foreground">Choose the way you'd like to support AlumniPulse™.</p>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="flex flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
              <p className="font-heading text-lg font-medium">One-Time Contribution</p>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">A single contribution toward AlumniPulse™ development, whenever you like.</p>
              <Button onClick={() => setOpen(true)} className="mt-5 rounded-full gap-1.5" data-testid="plan-onetime"><Heart size={16} weight="fill" /> Contribute Now</Button>
            </div>
            <div className="relative flex flex-col rounded-2xl border-2 border-secondary bg-card p-7 shadow-md">
              <span className="absolute -top-3 left-7 rounded-full bg-secondary px-3 py-0.5 text-xs font-medium text-secondary-foreground">Most popular</span>
              <p className="font-heading text-lg font-medium">Monthly Supporter</p>
              <p className="mt-1 font-heading text-3xl font-semibold text-primary">$9<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">Ongoing support while AlumniPulse™ grows — cancel any time.</p>
              <Button onClick={() => startCheckout("supporter_monthly", user)} className="mt-5 rounded-full gap-1.5" data-testid="plan-monthly"><HandHeart size={16} /> Become a Supporter</Button>
            </div>
            <div className="flex flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
              <p className="font-heading text-lg font-medium">Annual Supporter</p>
              <p className="mt-1 font-heading text-3xl font-semibold text-primary">$90<span className="text-sm font-normal text-muted-foreground">/yr</span></p>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">For individuals, alumni groups, institutions and organizations supporting all year.</p>
              <Button onClick={() => startCheckout("supporter_annual", user)} variant="outline" className="mt-5 rounded-full gap-1.5" data-testid="plan-annual">Choose Annual Support</Button>
            </div>
          </div>
          </div>
        </Section>

        {/* Global vision */}
        <Section className="pb-16">
          <div className="rounded-[2rem] bg-primary px-8 py-14 text-center text-primary-foreground sm:px-12">
            <GlobeHemisphereWest size={40} weight="duotone" className="mx-auto opacity-90" />
            <h2 className="mt-4 font-heading text-3xl font-light sm:text-4xl">One Global Network. Millions of Educational Journeys.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-primary-foreground/80">A place where a student can discover the history of their institution, alumni can find classmates from decades ago, teachers can reconnect with former students, and every generation can leave something behind for the next.</p>
            <p className="mt-8 font-heading text-2xl font-light italic sm:text-3xl">Every Institution. Every Generation. Every Connection.</p>
          </div>
        </Section>

        {/* Thank you */}
        <Section className="pb-20">
          <div className="rounded-[2rem] border border-secondary/25 bg-accent/40 p-8 text-center sm:p-14">
            <CheckCircle size={40} weight="fill" className="mx-auto text-secondary" />
            <h2 className="mt-4 font-heading text-3xl font-light tracking-tight sm:text-4xl">Thank You for Supporting AlumniPulse™</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">Whether you contribute financially, share a photograph, tell a story, suggest an institution, volunteer your time, or simply introduce AlumniPulse™ to your classmates — you are helping build something designed to last for generations.</p>
            <p className="mx-auto mt-4 max-w-2xl text-sm italic text-foreground/80">Your contribution doesn't have to be money. It can be a memory, a story, a photograph, an idea, a connection — or simply helping someone find their way back home.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/register"><Button size="lg" className="rounded-full gap-2" data-testid="thankyou-join">Join the AlumniPulse™ Community <ArrowRight size={18} /></Button></Link>
            </div>
            {/* Follow + support email */}
            <div className="mt-8 flex flex-col items-center gap-4 border-t border-border/60 pt-6">
              <div className="flex items-center gap-3">
                {SOCIALS.map((s, i) => (
                  <a key={i} href={s.href} target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-full bg-card text-primary shadow-sm transition-colors hover:bg-primary hover:text-primary-foreground" data-testid="support-social"><s.icon size={20} weight="fill" /></a>
                ))}
              </div>
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><EnvelopeSimple size={15} /> <a href="mailto:info@alumnipulse.net" className="hover:text-secondary">info@alumnipulse.net</a></p>
            </div>
          </div>
        </Section>
      </div>

      {/* Contribute amount modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="contribute-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">Contribute to AlumniPulse™</DialogTitle>
            <DialogDescription>Choose an amount. You'll be taken to a secure Stripe checkout.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3">
            {AMOUNTS.map(([key, label]) => (
              <button key={key} onClick={() => setAmt(key)} data-testid={`amt-${key}`}
                className={`rounded-xl border py-4 font-heading text-lg font-medium transition-colors ${amt === key ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 hover:border-secondary/40"}`}>{label}</button>
            ))}
          </div>
          <Button onClick={contribute} className="mt-2 w-full rounded-full gap-1.5" data-testid="contribute-confirm"><Heart size={16} weight="fill" /> Contribute Now</Button>
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck size={13} /> Secure payment by Stripe</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
